import { CaDialogComponent } from '../../../../shared/dialogs/ca-dialog.base'
import { Component, ElementRef, forwardRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AttachmentService } from '../../services/attachment.service'
import { DialogService } from '../../../../core/services/dialog.service'
import { ErrorService } from '../../../../core/services/error.service'
import { AttachmentRouteTypeEnum } from '../../enums/attachment-route-type.enum'
import { DocumentUploadViewModeEnum } from '../../../../shared/ui/document-upload/document-upload-view-mode.enum'
import { Observable, Subscription } from 'rxjs'
import { Store } from '@ngrx/store'
import { selectAttachmentTypeScanState } from '../../../../domain/attachment/components/attachment-type-scan-data/state/common-domain-components.selectors'
import { ContractTypeEnum } from '../../../../shared/enums/contract-type.enum'
import { ComponentModeEnum } from '../../../../shared/enums/component-mode.enum'
import { selectContractShowContractOwnersLength } from '../../../../features/contracts/contract/show/state/show.selectors'
import { selectContractEditOwnersLength } from '../../../../features/contracts/contract/edit/state/edit.selectors'
import { selectSubcontractShowContractOwnersLength } from '../../../../features/contracts/subcontract/show/state/subcontract-show.selectors'
import { selectSubcontractEditOwnersLength } from '../../../../features/contracts/subcontract/edit/state/subcontract-edit.selectors'
import {
    selectNonCadastralContractCreateOwnersLength,
} from '../../../../features/non-cadastral/non-cadastral-contract/create/state/non-cadastral-contract-create.selectors'
// Note: Purchase case selectors are NOT imported here to avoid circular dependency with lazy-loaded module
// Instead, ownersLength$ must be passed as optional parameter for PURCHASE task type
import { AttachmentUploadedType } from '../../../../api/backend'

@Component({
    selector: 'ca-add-attachment-dialog',
    templateUrl: './add-attachment-dialog.html',
    standalone: false
})
export class AddAttachmentDialogComponent extends CaDialogComponent<{
    entityId: number;
    mode: string;
    routeType: AttachmentRouteTypeEnum;
    task: string;
    // Optional: For lazy-loaded modules (like purchase-case), pass ownersLength$ to avoid circular dependency
    ownersLengthInput$?: Observable<number>;
}, boolean> implements OnInit, OnDestroy {

    @ViewChild('uploadComponent') private uploadComponent: ElementRef<HTMLElement>

    documentType = 'contract'
    entityId: number
    mode: string
    routeType: AttachmentRouteTypeEnum
    task: string
    ownersLengthInput$: Observable<number>
    loading = false
    file: Blob = null
    filename: string = null
    uploadedType: AttachmentUploadedType
    ownersLength: number
    checkedIds: number[]
    checkedAll: boolean
    viewMode: DocumentUploadViewModeEnum = DocumentUploadViewModeEnum.LABEL_BUTTON

    ownersLength$: Observable<number>
    #selectAttachmentTypeScanState: Subscription
    #ownersLength: Subscription

    constructor(@Inject(Store) private store: Store,
                @Inject(forwardRef(() => AttachmentService)) private attachmentService: AttachmentService,
                @Inject(forwardRef(() => DialogService)) private dialogService: DialogService,
                @Inject(ErrorService) private errorService: ErrorService,
                ) {
        super()

        this.#selectAttachmentTypeScanState = this.store.select(selectAttachmentTypeScanState).subscribe(data => {
            this.uploadedType = data.uploadedType
            this.checkedAll = data.checkedAll
            this.checkedIds = data.checkedIds
        })
    }

    ngOnInit(): void {
        switch (this.task) {
            case ContractTypeEnum.CONTRACT:
                this.ownersLength$ = this.mode === ComponentModeEnum.SHOW ? this.store.select(selectContractShowContractOwnersLength) : this.store.select(selectContractEditOwnersLength)
                break
            case ContractTypeEnum.SUBCONTRACT:
                this.ownersLength$ = this.mode === ComponentModeEnum.SHOW ? this.store.select(selectSubcontractShowContractOwnersLength) : this.store.select(selectSubcontractEditOwnersLength)
                break
            case ContractTypeEnum.NONCADASTRALCONTRACT:
                this.ownersLength$ = this.store.select(selectNonCadastralContractCreateOwnersLength)
                break
            case ContractTypeEnum.PURCHASE:
                // Use ownersLengthInput$ passed from purchase-case module to avoid circular dependency
                this.ownersLength$ = this.ownersLengthInput$
                break
        }
        if (this.ownersLength$) {
            this.#ownersLength = this.ownersLength$.subscribe(number => this.ownersLength = number)
        }
    }

    ngOnDestroy() {
        this.#selectAttachmentTypeScanState?.unsubscribe()
        this.#ownersLength?.unsubscribe()
    }

    closeModal(result?: boolean): void {
        super.result = result
        super.close()
    }

    selectFile(file: File): void {
        this.file = file
        this.filename = file?.name
    }

    saveFile(): void {
        if (this.validateForm()) {
            this.loading = true

            if (this.documentType === 'scanned') {
                this.attachmentService.uploadAttachmentScan(this.routeType, this.entityId, this.filename, this.file, this.uploadedType, this.checkedIds).then(() => {
                    this.closeModal(true)
                }, (error) => {
                    this.errorService
                     .onErrorResponseAlertUser(error, 'Při ukládání souboru došlo k chybě.')
                     .then(() => {
                         this.closeModal(false)
                     })
                })
            } else {
                this.attachmentService.uploadAttachment(this.routeType, this.entityId, this.filename, this.file).then(() => {
                    this.closeModal(true)
                }, (error) => {
                    this.errorService
                     .onErrorResponseAlertUser(error, 'Při ukládání souboru došlo k chybě.')
                     .then(() => {
                         this.closeModal(false)
                     })
                })
            }
        }
    }

    private validateForm(): boolean {
        if (!this.file) {
            this.dialogService.showWarningDialog('Je třeba vybrat soubor.')
            return false
        }

        if (this.documentType === 'scanned') {
            if (!this.uploadedType) {
                this.dialogService.showWarningDialog('Je třeba vybrat typ naskenovaného dokumentu.')
                return false
            }
            if (this.ownersLength > 1 && this.checkedIds.length === 0) {
                this.dialogService.showWarningDialog('Je třeba vybrat aspoň jednoho propachtovatele.')
                return false
            }
        }

        return true
    }

}
